import MissingImgSrc from '@/assets/image/missing_image.webp';
import { SpinLoader } from '@/components/loader/spin-loader';
import { fetchPictureSets, fetchWordPage } from '@/firebase/firebase-db';
import { getImageUrl } from '@/firebase/shiritori-db';
import type { ShiritoriWordDto } from '@/game/core/shiritori-word';
import { useCallback, useEffect, useRef, useState } from 'react';

const WORD_PAGE_SIZE = 100;
const visibleCallbacks = new WeakMap<Element, () => void>();
let imageObserver: IntersectionObserver | undefined;

function observeVisibility(element: Element, onVisible: () => void) {
    if (!('IntersectionObserver' in window)) {
        onVisible();
        return () => { };
    }

    if (!imageObserver) {
        imageObserver = new IntersectionObserver((entries) => {
            for (const entry of entries) {
                if (!entry.isIntersecting) {
                    continue;
                }

                const callback = visibleCallbacks.get(entry.target);
                if (callback) {
                    callback();
                }

                visibleCallbacks.delete(entry.target);
                imageObserver?.unobserve(entry.target);
            }
        }, { rootMargin: '400px' });
    }

    visibleCallbacks.set(element, onVisible);
    imageObserver.observe(element);

    return () => {
        visibleCallbacks.delete(element);
        imageObserver?.unobserve(element);
    };
}

export function CheatsView() {
    const [words, setWords] = useState<ShiritoriWordDto[]>([]);

    const [pictureSetId, setPictureSetId] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    const [hasError, setHasError] = useState(false);
    const [hasLoadMoreError, setHasLoadMoreError] = useState(false);
    const [nextCursor, setNextCursor] = useState<string>();
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const loadMoreRef = useRef<HTMLDivElement>(null);
    const isLoadingMoreRef = useRef(false);

    useEffect(() => {
        let isCurrent = true;

        Promise.all([
            fetchWordPage(undefined, WORD_PAGE_SIZE),
            fetchPictureSets(),
        ]).then(([firstPage, pictureSets]) => {
            if (!isCurrent) {
                return;
            }

            const firstPictureSet = Object.values(pictureSets || {})[0];
            if (!firstPictureSet) {
                throw new Error('No picture set is configured');
            }

            setWords(firstPage.words);
            setNextCursor(firstPage.nextCursor);
            setPictureSetId(firstPictureSet.pictureSetId);
        }).catch((error) => {
            console.error('[CheatsView]', error);
            if (isCurrent) {
                setHasError(true);
            }
        }).finally(() => {
            if (isCurrent) {
                setIsLoading(false);
            }
        });

        return () => {
            isCurrent = false;
        };
    }, []);

    const loadMore = useCallback(async () => {
        if (!nextCursor || isLoadingMoreRef.current) {
            return;
        }

        isLoadingMoreRef.current = true;
        setIsLoadingMore(true);
        setHasLoadMoreError(false);

        try {
            const page = await fetchWordPage(nextCursor, WORD_PAGE_SIZE);
            setWords((currentWords) => [...currentWords, ...page.words]);
            setNextCursor(page.nextCursor);
        } catch (error) {
            console.error('[CheatsView.loadMore]', error);
            setHasLoadMoreError(true);
        } finally {
            isLoadingMoreRef.current = false;
            setIsLoadingMore(false);
        }
    }, [nextCursor]);

    useEffect(() => {
        const element = loadMoreRef.current;
        if (!element || !nextCursor || hasLoadMoreError) {
            return;
        }

        const observer = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
                void loadMore();
            }
        }, {
            rootMargin: '800px',
        });

        observer.observe(element);
        return () => observer.disconnect();
    }, [hasLoadMoreError, loadMore, nextCursor]);

    if (isLoading) {
        return (
            <main className="flex min-h-full w-full items-center justify-center">
                <SpinLoader />
            </main>
        );
    }

    if (hasError) {
        return (
            <main className="flex min-h-full w-full items-center justify-center p-6">
                <p className="rounded border border-danger bg-background px-4 py-3 text-center text-sm text-foreground">
                    Unable to load the word gallery.
                </p>
            </main>
        );
    }

    return (
        <main className="w-full max-w-screen-2xl px-2 py-4 sm:px-4 lg:px-6">
            <h1 className="mb-4 text-center text-2xl font-semibold text-foreground">Word gallery</h1>
            <div className="grid grid-cols-5 gap-1 sm:grid-cols-6 sm:gap-2 md:grid-cols-8 lg:grid-cols-10 xl:grid-cols-12 2xl:grid-cols-14">
                {words.map((word) => (
                    <article key={word.wordId} className="min-w-0 overflow-hidden rounded border border-secondary/50 bg-background">
                        <ImageContainer pictureSetId={pictureSetId} word={word} />
                        <div className="border-t border-secondary/50 px-1 py-1.5 text-center">
                            <p className="truncate text-xs font-semibold text-foreground sm:text-sm" title={word.writtenForm}>
                                {word.writtenForm || word.kana}
                            </p>
                            {word.writtenForm !== word.kana && (
                                <p className="truncate text-[0.625rem] text-secondary sm:text-xs" title={word.kana}>
                                    {word.kana}
                                </p>
                            )}
                        </div>
                    </article>
                ))}
            </div>
            <div ref={loadMoreRef} className="flex min-h-20 w-full items-center justify-center py-4">
                {isLoadingMore && <SpinLoader />}
                {hasLoadMoreError && (
                    <p className="text-center text-sm text-danger">Unable to load more words.</p>
                )}
                {!nextCursor && words.length > 0 && (
                    <p className="text-center text-xs text-secondary">All words loaded.</p>
                )}
            </div>
        </main>
    );
}

function ImageContainer({ pictureSetId, word }: { pictureSetId: string; word: ShiritoriWordDto }) {
    const containerRef = useRef<HTMLDivElement>(null);
    const [shouldLoad, setShouldLoad] = useState(false);
    const [imageSrc, setImageSrc] = useState('');

    useEffect(() => {
        const element = containerRef.current;
        if (!element) {
            return;
        }

        return observeVisibility(element, () => setShouldLoad(true));
    }, []);

    useEffect(() => {
        if (!shouldLoad) {
            return;
        }

        let isCurrent = true;
        getImageUrl(pictureSetId, word.imageId).then((url) => {
            if (isCurrent) {
                setImageSrc(url || MissingImgSrc);
            }
        });

        return () => {
            isCurrent = false;
        };
    }, [pictureSetId, shouldLoad, word.imageId]);

    return (
        <div ref={containerRef} className="flex aspect-square w-full items-center justify-center p-1">
            {!imageSrc && <SpinLoader />}
            {imageSrc && (
                <img src={imageSrc} alt={word.writtenForm || word.kana} className="h-full w-full object-contain" decoding="async" loading="lazy" />
            )}
        </div>
    );
}
